import React, { useState } from 'react';
import { base44 } from '@/api/base44Client';
import { useQuery } from '@tanstack/react-query';
import { Loader2, X, Search, UserCheck } from 'lucide-react';

// Modal picker for bulk-assigning selected orders to a ConsumerAccount.
// Loads all consumer accounts, filters by search, and calls onPick(account).
export default function BulkAssignAccountModal({ onClose, onPick }) {
  const [search, setSearch] = useState('');
  const { data: accounts = [], isLoading } = useQuery({
    queryKey: ['bulk-assign-consumer-accounts'],
    queryFn: () => base44.entities.ConsumerAccount.list('-created_date', 500),
  });

  const q = search.toLowerCase();
  const filtered = (accounts || []).filter((a) => {
    if (!q) return true;
    return (
      a.customer_name?.toLowerCase().includes(q) ||
      a.sage_account_ref?.toLowerCase().includes(q) ||
      a.email?.toLowerCase().includes(q)
    );
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80" onClick={onClose}>
      <div className="w-full max-w-lg bg-[#111115] border border-white/10 p-6 max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="text-primary text-[10px] font-bold tracking-widest mb-1">BULK ASSIGN</p>
            <h3 className="text-white font-black text-sm uppercase">Assign Orders to Account</h3>
          </div>
          <button onClick={onClose} className="text-white/40 hover:text-white"><X className="w-5 h-5" /></button>
        </div>

        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, Sage ref or email…"
            className="seo-input pl-9"
            autoFocus
          />
        </div>

        <div className="flex-1 overflow-y-auto space-y-1 -mr-2 pr-2">
          {isLoading ? (
            <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-primary" /></div>
          ) : filtered.length === 0 ? (
            <p className="text-white/30 text-xs text-center py-10">No consumer accounts found</p>
          ) : (
            filtered.map((a) => (
              <button
                key={a.id}
                onClick={() => onPick(a.id, a.customer_name)}
                className="w-full flex items-center gap-3 border border-white/5 hover:border-primary/40 bg-[#0d0d0f] hover:bg-white/[0.03] px-3 py-2.5 text-left transition-colors"
              >
                <div className="w-8 h-8 bg-primary/10 flex items-center justify-center shrink-0 text-primary font-bold text-xs">
                  {a.customer_name?.charAt(0)?.toUpperCase() || '?'}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white text-sm font-medium truncate">{a.customer_name}</p>
                  <p className="text-white/40 text-xs truncate">{a.sage_account_ref}{a.email ? ` · ${a.email}` : ''}</p>
                </div>
                <UserCheck className="w-4 h-4 text-white/30 shrink-0" />
              </button>
            ))
          )}
        </div>
      </div>
    </div>
  );
}